import { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { getLogger } from '../../utilities/UseLog';
import { BaseDialog, DialogActionButton } from '../dialog-common';

const log = getLogger('NgpTwoButtonDialog');

// Default text constants
const DEFAULT_CONFIRM_TEXT = i18n._(t`Ok`);
const DEFAULT_CANCEL_TEXT = i18n._(t`Cancel`);

/**
 * A reusable two-button dialog component with Confirm and Cancel actions.
 *
 * Features:
 * - Customizable button text and visibility
 * - Warning mode for destructive actions
 * - Async action handling with loading state
 * - Configurable close behavior
 * - Backdrop click prevention option
 *
 * @component
 * @example
 * <NgpTwoButtonDialog
 *   title="Confirm Action"
 *   open={open}
 *   setOpen={setOpen}
 *   onConfirm={handleConfirm}
 *   isWarning
 * >
 *   <div>Are you sure?</div>
 * </NgpTwoButtonDialog>
 */
const NgpTwoButtonDialog = (props) => {
    const {
        title,
        children,
        open,
        setOpen,
        onCloseFunc,
        onConfirm,
        callerWillCloseOnConfirm = false,
        confirmButtonDisabled = false,
        confirmButtonText = DEFAULT_CONFIRM_TEXT,
        cancelButtonText = DEFAULT_CANCEL_TEXT,
        showConfirmButton = true,
        isWarning = false,
        disableBackdropClick = false,
        closeInTitleBar = true,
        confirmButtonProps,
        maxWidth = 'md',
        fullWidth = false,
        paperProps,
    } = props;

    const [isActioning, setIsActioning] = useState(false);
    const dialogRef = useRef(null);

    /**
     * Handles cancel button click by delegating to BaseDialog's handleOnClose
     */
    const handleCancelClick = useCallback((event, reason) => {
        if (dialogRef.current && dialogRef.current.handleOnClose) {
            dialogRef.current.handleOnClose(event, reason);
        }
    }, []);

    /**
     * Handles confirm button click with error handling
     */
    const handleConfirmClick = useCallback(async () => {
        if (!onConfirm || isActioning) {
            return;
        }

        setIsActioning(true);
        try {
            await onConfirm();
        } catch (error) {
            log.error('Error in onConfirm:', error);
        } finally {
            setIsActioning(false);

            if (!callerWillCloseOnConfirm) {
                setOpen(false);
            }
        }
    }, [onConfirm, isActioning, callerWillCloseOnConfirm, setOpen]);

    /**
     * Memoized confirm button
     */
    const confirmButton = useMemo(
        () => (
            <DialogActionButton
                isWarning={isWarning}
                disabled={isWarning ? isActioning : confirmButtonDisabled || isActioning}
                onClick={handleConfirmClick}
                text={confirmButtonText}
                buttonProps={confirmButtonProps}
            />
        ),
        [isWarning, isActioning, confirmButtonDisabled, handleConfirmClick, confirmButtonText, confirmButtonProps]
    );

    /**
     * Memoized dialog actions
     */
    const actions = useMemo(
        () => (
            <>
                <Button disabled={isActioning} onClick={handleCancelClick} tabIndex={-1}>
                    {cancelButtonText}
                </Button>
                {showConfirmButton && confirmButton}
            </>
        ),
        [isActioning, handleCancelClick, cancelButtonText, showConfirmButton, confirmButton]
    );

    return (
        <BaseDialog
            ref={dialogRef}
            title={title}
            open={open}
            setOpen={setOpen}
            onCloseFunc={onCloseFunc}
            actions={actions}
            disableBackdropClick={disableBackdropClick}
            closeInTitleBar={closeInTitleBar}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            paperProps={paperProps}
            isActioning={isActioning}
            onActioningChange={setIsActioning}
            dialogId="two-button-dialog"
        >
            {children}
        </BaseDialog>
    );
};

NgpTwoButtonDialog.propTypes = {
    /** Dialog title */
    title: PropTypes.node.isRequired,
    /** Dialog content */
    children: PropTypes.node,
    /** Controls whether the dialog is open */
    open: PropTypes.bool.isRequired,
    /** Function to set the open state */
    setOpen: PropTypes.func.isRequired,
    /** Optional callback when dialog closes */
    onCloseFunc: PropTypes.func,
    /** Callback for confirm button click */
    onConfirm: PropTypes.func,
    /** If true, caller is responsible for closing after confirm */
    callerWillCloseOnConfirm: PropTypes.bool,
    /** Disables the confirm button */
    confirmButtonDisabled: PropTypes.bool,
    /** Custom text for confirm button */
    confirmButtonText: PropTypes.node,
    /** Custom text for cancel button */
    cancelButtonText: PropTypes.node,
    /** Shows/hides the confirm button */
    showConfirmButton: PropTypes.bool,
    /** If true, buttons use warning (error) styling */
    isWarning: PropTypes.bool,
    /** If true, prevents closing on backdrop click */
    disableBackdropClick: PropTypes.bool,
    /** Shows/hides close button in title bar */
    closeInTitleBar: PropTypes.bool,
    /** Additional props for confirm button */
    confirmButtonProps: PropTypes.object,
    /** Maximum width of the dialog */
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
    /** If true, dialog stretches to maxWidth */
    fullWidth: PropTypes.bool,
    /** Props applied to the Paper element */
    paperProps: PropTypes.object,
};

NgpTwoButtonDialog.defaultProps = {
    children: null,
    onCloseFunc: null,
    onConfirm: null,
    callerWillCloseOnConfirm: false,
    confirmButtonDisabled: false,
    confirmButtonText: DEFAULT_CONFIRM_TEXT,
    cancelButtonText: DEFAULT_CANCEL_TEXT,
    showConfirmButton: true,
    isWarning: false,
    disableBackdropClick: false,
    closeInTitleBar: true,
    confirmButtonProps: null,
    maxWidth: 'md',
    fullWidth: false,
    paperProps: undefined,
};

export default memo(NgpTwoButtonDialog);
